import { useContext } from "react";
import { ToolContext } from "../../context/ToolContext";
import { Layer } from "leaflet";
import * as tab from "../../constants/tabs";
import {
  getDistance,
  recalculatePolygonPositionsBasedOnWidthAndHeight,
} from "../../utils/sitePlanUtils";

export interface SitePlan {
  id: string;
  name: string;
  positions: [number, number][];
  width: number;
  height: number;
  opacity: number;
  image: string;
  lockSitePlanPosition: boolean;
  uniformScaling: boolean;
}

export const useSitePlan = () => {
  const { toolState, setToolState } = useContext(ToolContext);
  const { sitePlan, originalSitePlan, sitePlanSelected, leafletMap } = toolState;

  const selectSitePlan = () => {
    if (toolState.drawCable) return;
    if (toolState.mode === "select-results") return;

    setToolState({
      ...toolState,
      sitePlanSelected: true,
      activeTool: "",
      activeCategory: 0,
      clickedAsset: null,
      drawMarker: false,
      drawCable: false,
      drawBoundary: false,
      ringfenced: [],
      ringfencedFiltered: [],
      ringfencedTypes: [],
      disableBoundary: false,
      activeTab: tab.PROPERTIES,
      searchValue: null,
      matchedSearchIds: [],
    });
  };

  const removeTransformPathLayers = () => {
    if (leafletMap) {
      leafletMap.eachLayer(function (layer: Layer & { transform?: any }) {
        if (layer.transform && layer.transform._handlersGroup) {
          const transformLayers: Layer[] = Object.values(
            layer.transform._handlersGroup._layers,
          ).map((l: any) => l as Layer);
          transformLayers.forEach((lyr: Layer) => {
            leafletMap.removeLayer(lyr);
          });
        }
      });
    }
  };

  const toggleUniformScaling = () => {
    setToolState({
      ...toolState,
      sitePlan: {
        ...toolState.sitePlan,
        uniformScaling: toolState.sitePlan ? !toolState.sitePlan.uniformScaling : false,
      },
    });
  };

  const setOpacity = (opacity: number): void => {
    setToolState({
      ...toolState,
      sitePlan: {
        ...toolState.sitePlan,
        opacity: opacity,
      },
    });
  };

  const toggleLockSitePlanPosition = () => {
    setToolState({
      ...toolState,
      sitePlan: {
        ...toolState.sitePlan,
        lockSitePlanPosition: toolState.sitePlan ? !toolState.sitePlan.lockSitePlanPosition : false,
      },
    });
  };

  const setSitePlanPositions = (
    positions: [number, number][][],
    width?: number,
    height?: number,
  ): void => {
    if (!sitePlan) return;
    setToolState({
      ...toolState,
      sitePlan: {
        ...toolState.sitePlan,
        positions: positions[0],
        width: width ? width : parseFloat(getDistance(positions[0][0], positions[0][1]).toFixed(2)),
        height: height
          ? height
          : parseFloat(getDistance(positions[0][0], positions[0][3]).toFixed(2)),
      },
    });
    removeTransformPathLayers();
  };

  const setSitePlanDimension = (dimension: "width" | "height", value: number): void => {
    if (!sitePlan || isNaN(value)) return;

    const isWidth = dimension === "width";

    const [newWidth, newHeight] = isWidth
      ? [
          value,
          sitePlan.uniformScaling ? sitePlan.height * (value / sitePlan.width) : sitePlan.height,
        ]
      : [
          sitePlan.uniformScaling ? sitePlan.width * (value / sitePlan.height) : sitePlan.width,
          value,
        ];

    const newPositions = [
      recalculatePolygonPositionsBasedOnWidthAndHeight(sitePlan.positions, newHeight, newWidth),
    ];

    setSitePlanPositions(newPositions, newWidth, newHeight);
  };

  const resetSitePlan = () => {
    setToolState({
      ...toolState,
      sitePlan: {
        ...originalSitePlan,
      },
      sitePlanSelected: true,
    });

    removeTransformPathLayers();

    const importSitePlanPdf = document.getElementById(
      "importSitePlanPdf",
    ) as HTMLInputElement | null;
    if (importSitePlanPdf) {
      importSitePlanPdf.value = "";
    }

    const loadSitePlanPdf = document.getElementById("loadSitePlanPdf") as HTMLInputElement | null;
    if (loadSitePlanPdf) {
      loadSitePlanPdf.value = "";
    }
  };

  const deleteSitePlan = () => {
    setToolState({
      ...toolState,
      sitePlan: null,
      originalSitePlan: null,
      sitePlanSelected: false,
    });
    removeTransformPathLayers();

    const importSitePlanPdf = document.getElementById(
      "importSitePlanPdf",
    ) as HTMLInputElement | null;
    if (importSitePlanPdf) {
      importSitePlanPdf.value = "";
    }

    const loadSitePlanPdf = document.getElementById("loadSitePlanPdf") as HTMLInputElement | null;
    if (loadSitePlanPdf) {
      loadSitePlanPdf.value = "";
    }
  };

  return {
    selectSitePlan,
    sitePlan,
    toggleUniformScaling,
    toggleLockSitePlanPosition,
    setSitePlanDimension,
    resetSitePlan,
    deleteSitePlan,
    setOpacity,
    setSitePlanPositions,
    sitePlanSelected,
    removeTransformPathLayers,
  };
};
